import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, startTransition, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbarBottomInset } from "../../../contexts/SnackbarContext";
import { BOTTOM_NAV } from "../../../theme/tokens";
import { staffingWeeklyPath } from "../../../utils/staffingRoutes";
import { countRequestsWithResponses } from "../../../utils/utilFunctions";
import { type BottomNavItem, SystemBottomNav } from "../../UIToolkit/Navigation/SystemBottomNav/SystemBottomNav";
import { GapsMoreSheet } from "./GapsMoreSheet";
import { staffingDayKey } from "./gapsRequests";
import { closeGapsSheet, type GapsSheetIntent, GapsSheetKind, openGapsSheet, useGapsSheet } from "./useGapsSheet";

/** The bar's destinations. Stable strings: they are the test-id suffixes and the analytics property. */
export enum GapsNavSlot {
	Home = "home",
	OpenShifts = "open-shifts",
	Post = "post",
	More = "more",
}

const ISO_DATE_IN_PATH = /\/(\d{4}-\d{2}-\d{2})(\/|$)/;

/** Which slot the current URL is on, or `undefined` on a page the bar does not own. */
const slotForPath = (pathname: string): GapsNavSlot | undefined => {
	if (/\/staffing(\/|$)/.test(pathname) && !/\/staffing\/classic(\/|$)/.test(pathname)) return GapsNavSlot.Home;
	if (/\/requests(\/|$)/.test(pathname)) return GapsNavSlot.OpenShifts;
	if (/\/post-shift(\/|$)/.test(pathname)) return GapsNavSlot.Post;
	return undefined;
};

type Props = {
	/** Opens full-screen Annie, forwarded to the More sheet so it offers exactly what the hamburger does. */
	onOpenAnnie?: () => void;
};

/**
 * The gaps-first view's bottom navigation bar.
 *
 * Mounted by `Layout`, not by a page, because it is a property of the SESSION rather than of one route:
 * it replaces the hamburger for as long as a user is in the gaps-first view, so it has to survive every
 * navigation the bar itself offers. Mounted per-page it vanished the moment you tapped "Open shifts",
 * which is the one thing a persistent bar exists not to do.
 *
 * Portalled to `body` so nothing between here and the root can trap it in a lower stacking context: it
 * has to out-rank the More sheet's `Modal` root, which is itself a fixed full-viewport layer — otherwise
 * the sheet's own scrim swallows every tap on the bar that opened it.
 *
 * One step above the DRAWER layer and below the MODAL layer, never above modals (P3-13). Every sheet is an MUI
 * `Drawer` (`zIndex.drawer`) that stops above the bar, so the bar has to win over their full-viewport roots to
 * stay tappable. A blocking dialog — a warning, a confirm, the overlap-merge prompt, the agency choice — is a
 * `Dialog` on `zIndex.modal`, and must cover the bar: sitting above them (`modal + 1`, as the bar first shipped)
 * left "Open shifts" and "More" live under a question the user had not answered yet.
 */
export const GapsBottomNav: FC<Props> = ({ onOpenAnnie }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { staffRequests } = useRequestsContext();
	const { intent } = useGapsSheet();
	const [isMoreOpen, setIsMoreOpen] = useState<boolean>(false);
	// Toasts clear the bar for as long as it is on screen (P3-16); everywhere else they keep their usual place.
	useSnackbarBottomInset(BOTTOM_NAV.TOTAL_HEIGHT_CSS);
	// The post sheet IS the Post slot's destination now, so its slot reads as current while it is up.
	const isPostSheetOpen = intent?.kind === GapsSheetKind.PostShift;
	// The slot the user just tapped, held until the URL agrees.
	//
	// `activeValue` is derived from the pathname, which only changes once the router commits AND the new
	// page has rendered — so on a heavy screen the indicator sat on the old slot for as long as that took,
	// and the bar felt unresponsive to a tap it had already received. Marking is a statement about what
	// the user did, which is known immediately; the page catching up is the slow part and is what gets
	// deferred. Cleared by the pathname effect below, so the URL remains the source of truth.
	const [tappedSlot, setTappedSlot] = useState<GapsNavSlot | undefined>(undefined);

	const departmentId = chosenDepartment.department_id;

	// The navigation landed (or went somewhere else entirely, e.g. the back button) — let the URL speak.
	useEffect(() => setTappedSlot(undefined), [pathname]);

	// The day the user is looking at, so a quick action opens on it rather than on today. Read from the
	// URL because the bar is outside the page that owns the date.
	const contextDateISO = useMemo<string>(() => {
		const match = pathname.match(ISO_DATE_IN_PATH);
		const fromPath = match ? DateTime.fromISO(match[1]) : undefined;
		// DEFAULT: a page with no day in its URL (the undated home, Open Shifts) is on today — the day the undated home
		// itself opens on (`MobileGapsShell`), so a sheet opened from here lands on the day it was bound to.
		return staffingDayKey(fromPath?.isValid ? fromPath : DateTime.now());
	}, [pathname]);

	// The approvable pickups — requests a nurse has actually responded to — not every open request. Same
	// count the desktop Staffing header badges, from the same shared predicate.
	const awaitingApprovalCount = useMemo<number>(() => countRequestsWithResponses(staffRequests), [staffRequests]);

	const navItems = useMemo<BottomNavItem<GapsNavSlot>[]>(
		() => [
			{ value: GapsNavSlot.Home, label: t("gaps.nav.home"), icon: <DashboardOutlinedIcon /> },
			{
				value: GapsNavSlot.OpenShifts,
				label: t("gaps.nav.openShifts"),
				icon: <PendingActionsOutlinedIcon />,
				badge:
					awaitingApprovalCount > 0
						? { content: awaitingApprovalCount, color: "error", ariaLabel: t("gaps.nav.awaitingApproval", { count: awaitingApprovalCount }) }
						: undefined,
			},
			{ value: GapsNavSlot.Post, label: t("gaps.nav.post"), icon: <AddCircleOutlineIcon /> },
			{ value: GapsNavSlot.More, label: t("gaps.nav.more"), icon: <MoreHorizIcon /> },
		],
		[t, awaitingApprovalCount],
	);

	// A sheet on the home, opened from wherever the bar is. The intent is opened first and outlives the navigation, so
	// from any other page the home mounts, finds it and opens the sheet itself; on the home it opens in place.
	const openOnHome = (sheet: GapsSheetIntent): void => {
		setIsMoreOpen(false);
		openGapsSheet(sheet);
		if (slotForPath(pathname) === GapsNavSlot.Home) return;
		setTappedSlot(GapsNavSlot.Home);
		startTransition(() => navigate(staffingWeeklyPath(departmentId, contextDateISO)));
	};

	const handleNavChange = (slot: GapsNavSlot): void => {
		track("gaps_bottom_nav_clicked", { slot, department_id: departmentId });
		// Toggles. The bar deliberately sits ABOVE the sheet's scrim, so "More" stays tappable while the
		// sheet is open — and the only thing a second tap on an open menu can mean is "close it".
		if (slot === GapsNavSlot.More) {
			setIsMoreOpen((wasOpen) => !wasOpen);
			return;
		}
		// Post is the post sheet (P3-9), started from nothing — it offers the day's short shifts to prefill from. A second
		// tap while it is up closes it, as More's does.
		if (slot === GapsNavSlot.Post) {
			if (isPostSheetOpen) {
				closeGapsSheet();
				return;
			}
			track("gaps_post_shift_opened", { department_id: departmentId, source: "bottom_nav" });
			openOnHome({ kind: GapsSheetKind.PostShift, dayISO: contextDateISO, groupKey: null });
			return;
		}
		// Every other slot is a destination, so it takes the menu — and any open sheet — down on the way. The bar
		// stays tappable over both, so without this you would land on the new page behind a menu still covering
		// it; and a sheet would outlive the tap entirely whenever the destination is the page already on screen
		// (Gaps on today's home), leaving the tap looking dead.
		setIsMoreOpen(false);
		closeGapsSheet();
		// Mark first, in this render: the indicator is what tells the user the tap registered.
		setTappedSlot(slot);
		const destination = slot === GapsNavSlot.Home ? `/${departmentId}/staffing` : `/${departmentId}/requests`;
		// The destination page's render is the expensive half and nothing on the bar depends on it, so it
		// goes in a transition and cannot hold up painting the mark above.
		startTransition(() => navigate(destination));
	};

	// Each quick action is bound to the day it opens on — the day on screen, which is also where it navigates.
	const handleLogCallIn = (): void => openOnHome({ kind: GapsSheetKind.CallIn, dayISO: contextDateISO });

	// More → "Add to a shift": the add sheet from nothing, which asks which shift first.
	const handleAddToShift = (): void => openOnHome({ kind: GapsSheetKind.AddStaff, dayISO: contextDateISO, scope: null, linkedRequestId: null });

	return (
		<>
			{createPortal(
				<SystemBottomNav
					items={navItems}
					// The open sheet IS where you are, so the slot that opened it reads as current.
					activeValue={isMoreOpen ? GapsNavSlot.More : isPostSheetOpen ? GapsNavSlot.Post : (tappedSlot ?? slotForPath(pathname))}
					onChange={handleNavChange}
					ariaLabel={t("gaps.nav.ariaLabel")}
					dataTestId="gaps-bottom-nav"
					sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: theme.zIndex.drawer + 1 }}
				/>,
				document.body,
			)}
			<GapsMoreSheet
				open={isMoreOpen}
				onClose={() => setIsMoreOpen(false)}
				date={contextDateISO}
				onOpenAnnie={onOpenAnnie}
				onLogCallIn={handleLogCallIn}
				onAddToShift={handleAddToShift}
			/>
		</>
	);
};
